<template lang="pug">
div
  //- Compact Header with Key Info (includes extra info) - Stripe specific
  div(v-if="isStripePayment && hasCardDetails" class="mb-3")
    div(class="flex flex-col sm:flex-row sm:justify-between sm:items-baseline gap-2")
      div(class="flex flex-wrap items-baseline gap-2 sm:gap-3")
        //- Amount
        span.text-lg.font-semibold {{ formatCurrency(totalFee, currency) }}
        //- Status
        span(class="px-2 py-0.5 text-xs rounded" :class="statusClass") {{ status?.toUpperCase() || 'PENDING' }}
        //- Company
        span.text-xs.text-muted-foreground via {{ companyName }}
        //- Extra info tags
        template(v-if="extraStrings.length > 0")
          span(class="hidden sm:inline text-muted-foreground text-xs") •
          span(v-for="str in extraStrings" :key="str" class="text-xs px-1.5 py-0.5 bg-muted/70 rounded") {{ str }}
      a(v-if="stripePaymentId" :href="stripeDashboardUrl" target="_blank" class="text-xs text-blue-600 hover:underline inline-flex items-center gap-1")
        | Payment {{ '#' + data.id }}
        Icon(name="lucide:external-link" class="w-3 h-3")
  
  //- Simplified Payment Info Grid (includes extra info) - Generic
  div(v-else-if="hasPaymentInfo" class="mb-3")
    //- Main info row with extra tags
    div(class="flex flex-col sm:flex-row sm:justify-between sm:items-baseline gap-2 mb-3")
      div(class="flex flex-wrap items-baseline gap-2 sm:gap-3")
        span.text-lg.font-semibold {{ formatCurrency(totalFee, currency) }}
        span(class="px-2 py-0.5 text-xs rounded" :class="statusClass") {{ status?.toUpperCase() || 'PENDING' }}
        span.text-xs.text-muted-foreground {{ channel || companyName || 'Card' }}
        template(v-if="extraStrings.length > 0")
          span(class="hidden sm:inline text-muted-foreground text-xs") •
          span(v-for="str in extraStrings" :key="str" class="text-xs px-1.5 py-0.5 bg-muted/70 rounded") {{ str }}
      span.text-xs.text-muted-foreground(v-if="data.created_at") {{ formatDateShort(data.extra?.completed_at || data.created_at) }}
    //- Reference info if any (only for non-PayPal and non-Paymentwall)
    div(v-if="shouldShowReference")
      p(class="text-xs text-muted-foreground mb-0.5") Reference
      a(v-if="sessionId" :href="stripeSessionUrl" target="_blank" class="text-sm text-blue-600 hover:underline inline-flex items-center gap-1")
        | {{ truncatedSessionId }}
        Icon(name="lucide:external-link" class="w-3 h-3")
      p.text-sm.font-mono(v-else) {{ tradeId || captureId }}
</template>

<script lang="ts" setup>
import { computed } from 'vue'

const props = defineProps<{
  data: any
  hasCardDetails?: boolean
}>()

// Extract payment information
const sessionId = computed(() => props.data?.session_id)
const orderId = computed(() => props.data?.order_id)
const pmwRef = computed(() => props.data?.pmw_ref)
const tradeId = computed(() => props.data?.trade_id)
const captureId = computed(() => props.data?.capture_id)
const status = computed(() => props.data?.status)
const channel = computed(() => props.data?.channel || props.data?.extra?.channel)

const totalFee = computed(() => {
  // For Paymentwall, show the actual USD amount charged
  if (props.data?.channel === 'Paymentwall' && props.data?.extra?.pmw?.REVENUE_PAYMENT_LOCAL) {
    return Number(props.data.extra.pmw.REVENUE_PAYMENT_LOCAL)
  }
  return props.data?.total_fee || 0
})

const currency = computed(() => {
  // For Paymentwall, use the actual currency from pmw data
  if (props.data?.channel === 'Paymentwall' && props.data?.extra?.pmw?.CURRENCY_CODE) {
    return props.data.extra.pmw.CURRENCY_CODE
  }
  return props.data?.extra?.currency || props.data?.currency || 'EUR'
})

// Check if this is a Stripe payment
const isStripePayment = computed(() => {
  return !!(
    props.data?.pi_id ||
    props.data?.payment_id ||
    props.data?.session_id ||
    props.data?.extra?.project_name ||
    (props.data?.channel && props.data.channel.toLowerCase().includes('stripe'))
  )
})

// Get Stripe payment ID for dashboard link
const stripePaymentId = computed(() => props.data?.pi_id || props.data?.payment_id)

// Get Stripe dashboard URL
const stripeDashboardUrl = computed(() => {
  if (!stripePaymentId.value) return ''
  const isTest = props.data?.session_id?.includes('_test_')
  return `https://dashboard.stripe.com/${isTest ? 'test/' : ''}payments/${stripePaymentId.value}`
})

// Status styling class
const statusClass = computed(() => {
  const s = status.value?.toLowerCase()
  if (s === 'complete' || s === 'success' || s === 'succeeded') return 'bg-green-500/10 text-green-700 dark:bg-green-500/20 dark:text-green-400'
  if (s === 'pending' || s === 'processing') return 'bg-yellow-500/10 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-400'
  if (s === 'failed' || s === 'cancelled') return 'bg-red-500/10 text-red-700 dark:bg-red-500/20 dark:text-red-400'
  if (s === 'refund' || s === 'refunded') return 'bg-blue-500/10 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400'
  return 'bg-gray-500/10 text-gray-700 dark:bg-gray-500/20 dark:text-gray-400'
})

// Get company name based on project_name (for Stripe payments)
const companyName = computed(() => {
  const projectName = props.data?.extra?.project_name
  const companyMap = {
    PandaBX: 'Beexio',
    PandaTL: 'Teamleuk',
    PandaLP: 'Linppo',
    PandaFan: 'Beexio',
    PandaPro: 'Teamleuk',
  }
  return companyMap[projectName as string] || 'Beexio'
})

const truncatedSessionId = computed(() => {
  if (!sessionId.value) return ''
  const id = sessionId.value
  if (id.length <= 20) return id
  return `${id.substring(0, 10)}...${id.substring(id.length - 8)}`
})

const stripeSessionUrl = computed(() => {
  if (!sessionId.value) return ''
  const isTest = sessionId.value.includes('_test_')
  const paymentId = props.data?.pi_id || props.data?.payment_id
  if (paymentId) {
    return `https://dashboard.stripe.com/${isTest ? 'test/' : ''}payments/${paymentId}`
  }
  return `https://dashboard.stripe.com/${isTest ? 'test/' : ''}checkout/sessions/${sessionId.value}`
})

const hasPaymentInfo = computed(() => sessionId.value || orderId.value || pmwRef.value || tradeId.value || captureId.value || totalFee.value || status.value || channel.value)

// Only show reference for non-PayPal and non-Paymentwall payments
const shouldShowReference = computed(() => {
  const isPayPal = channel.value === 'PayPal'
  const isPaymentwall = channel.value === 'Paymentwall'
  const hasReference = sessionId.value || tradeId.value || captureId.value

  return hasReference && !isPayPal && !isPaymentwall
})

// Extra information strings array
const extraStrings = computed(() => {
  const ret: string[] = []
  const extra = props.data?.extra || {}

  // Balance change
  if ('new_balance' in extra && extra.old_balance !== extra.new_balance) {
    const curr = extra.system_currency || 'EUR'
    ret.push(`Balance: ${formatCurrency(extra.old_balance, curr)} → ${formatCurrency(extra.new_balance, curr)}`)
  }

  // Auto buy information
  if ('auto_buy' in extra) {
    const autoBuyType = extra.auto_buy_type || extra.auto_buy
    if (autoBuyType === 'home_quote') {
      ret.push(`Add Data (${extra.auto_buy_month}GB)`)
    } else if (autoBuyType === 'allplatform_quote') {
      ret.push(`Standard Data (${extra.auto_buy_month}GB)`)
    } else if (autoBuyType === 'pre') {
      ret.push('Relay Data')
    } else {
      const planNames: Record<string, string> = {
        '2': 'Basic',
        '4': 'Pro',
        '5': 'Basic',
        '6': 'Pro',
      }
      const planName = planNames[extra.auto_buy] || `Plan ${extra.auto_buy}`
      ret.push(`${planName} (${extra.auto_buy_month}mo)`)
    }
  }

  // From app
  if (extra.from_app) {
    ret.push('Via App')
  }

  // Reason
  if (extra.reason && extra.reason.length < 50) {
    ret.push(extra.reason)
  }

  // Manual add by admin
  if (extra.manual_add_by) {
    ret.push(`Admin #${extra.manual_add_by}`)
  }

  return ret
})

function formatCurrency(amount: number, currency: string) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
  }).format(amount)
}

function formatDateShort(timestamp: number | string) {
  const date = new Date(timestamp)
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
}
</script>
